import { DEFAULT_LEADS_SEARCH_PARAMS } from "common/constants";
import type {
  FacetParam,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

export default function useLeadsSearchParams(
  searchParams: LeadsSearchParams<SelectedFacetParam>,
  setSearchParams: (
    searchParams: Partial<LeadsSearchParams<SelectedFacetParam>>,
  ) => void,
) {
  function toggleSearchParam(
    key: keyof Omit<LeadsSearchParams<never>, "keywords" | "favorite">,
    value: SelectedFacetParam,
  ) {
    if (
      searchParams[key].some(({ id }) =>
        Array.isArray(value.id) && Array.isArray(id)
          ? value.id.every((string) => id.includes(string))
          : value.id === id,
      )
    ) {
      setSearchParams({
        [key]: (searchParams[key] as FacetParam[]).filter((item) =>
          Array.isArray(value.id) && Array.isArray(item.id)
            ? !value.id.every((string) => item.id.includes(string))
            : value.id !== item.id,
        ),
      });
    } else {
      setSearchParams({
        [key]: [
          ...(searchParams[key] as FacetParam[]),
          { id: value.id, name: value.name },
        ],
      });
    }
  }
  function resetSearchParam(param: keyof LeadsSearchParams<never>) {
    setSearchParams({
      [param]: DEFAULT_LEADS_SEARCH_PARAMS[param],
    });
  }
  function setSearchParam<T extends keyof LeadsSearchParams<never>>(
    key: T,
    value: LeadsSearchParams<never>[T],
  ) {
    setSearchParams({ [key]: value });
  }

  function resetAllParams() {
    setSearchParams(DEFAULT_LEADS_SEARCH_PARAMS);
  }

  function resetAllFilters() {
    setSearchParams({
      ...DEFAULT_LEADS_SEARCH_PARAMS,
      keywords: searchParams.keywords,
    });
  }
  const { keywords: _, ...filters } = searchParams;
  const hasFilters = Object.values(filters).some((filter) =>
    Array.isArray(filter) ? !!filter.length : !!filter,
  );

  return {
    setSearchParam,
    toggleSearchParam,
    resetSearchParam,
    resetAllParams,
    resetAllFilters,
    hasFilters,
  };
}
